/**
 * MusicManager.jsx
 * ─────────────────
 * Mount once inside <App> (or the root layout), above all routes.
 * Reads musicEnabled + volume from SettingsContext and drives a single
 * <audio> element that loops the background track.
 *
 * Rules satisfied:
 *  • One global audio element — never duplicated across page changes.
 *  • Music starts automatically when the component mounts (after first user
 *    interaction; browser autoplay policy is handled gracefully).
 *  • Music/mute state persists via SettingsContext → localStorage.
 *  • Volume slider in Settings drives the audio volume in real time.
 *  • Switching routes does NOT restart the track.
 */

import { useEffect, useRef } from 'react';
import { useSettings } from './Settingscontext';

/* ── Replace this path with your actual music file once you add one. ──────── */
/* The component degrades silently if the file is missing.                     */
import MUSIC_SRC from '../../audio/backgroundmusicprismpals.mp3';

export default function MusicManager() {
  const { musicEnabled, volume } = useSettings();
  const audioRef = useRef(null);
  const startedRef = useRef(false);

  /* Create the audio element once */
  useEffect(() => {
    const audio = new Audio(MUSIC_SRC);
    audio.loop   = true;
    audio.volume = 0.7; // default; overridden below
    audioRef.current = audio;

    /* Try to play immediately (works if a prior user gesture exists) */
    tryPlay(audio);

    /* Fallback: start on first user interaction */
    const handleInteraction = () => {
      if (!startedRef.current) tryPlay(audio);
    };
    window.addEventListener('click',     handleInteraction, { once: true });
    window.addEventListener('keydown',   handleInteraction, { once: true });
    window.addEventListener('touchstart',handleInteraction, { once: true });

    return () => {
      audio.pause();
      audio.src = '';
      window.removeEventListener('click',      handleInteraction);
      window.removeEventListener('keydown',    handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* React to musicEnabled toggle */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (musicEnabled) {
      tryPlay(audio);
    } else {
      audio.pause();
    }
  }, [musicEnabled]);

  /* React to volume slider (0-100 → 0-1) */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = musicEnabled ? volume / 100 : 0;
  }, [volume, musicEnabled]);

  /* No DOM output — this is a pure side-effect component */
  return null;
}

function tryPlay(audio) {
  if (!audio) return;
  audio.play()
    .then(() => { /* playing */ })
    .catch(() => { /* autoplay blocked — will retry on first interaction */ });
}